import {
  format24HourTime,
  format24HourDateTime,
  formatDate,
  formatDisplayDate,
  formatBookingId,
  getTimeAgo
} from '../../../utils/formatters';

export {
  format24HourTime,
  format24HourDateTime,
  formatDate,
  formatDisplayDate,
  formatBookingId,
  getTimeAgo
};

export {
  IATA_CITIES,
  AIRCRAFT_NAMES,
  TRACKING_TABS,
  TRACKING_BANNERS,
  tabVariants,
  DAYS_FILTER_OPTIONS,
  FLIGHT_STATUS_CONFIG,
  mapContainerStyle,
  premiumMapStyle,
  markerIcon,
  liveColumns,
  liveGhostRows,
  flightColumns,
  flightGhostRows,
  trainColumns,
  trainGhostRows
} from '../../../data/tracking/trackingData';

/**
 * Multi-format date parser handling DD-MM-YYYY, YYYY-MM-DD, ordinal suffixes, etc.
 */
export const parseAnyDate = (dateStr) => {
  if (!dateStr || dateStr === 'N/A') return null;
  try {
    const trimmed = String(dateStr).trim();
    // Match DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = trimmed.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (dmyMatch) {
      const d = new Date(parseInt(dmyMatch[3], 10), parseInt(dmyMatch[2], 10) - 1, parseInt(dmyMatch[1], 10));
      if (!isNaN(d.getTime())) return d;
    }
    // Match YYYY-MM-DD
    const ymdMatch = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
    if (ymdMatch) {
      const d = new Date(parseInt(ymdMatch[1], 10), parseInt(ymdMatch[2], 10) - 1, parseInt(ymdMatch[3], 10));
      if (!isNaN(d.getTime())) return d;
    }
    // Fallback for month strings e.g. "17-Sep-2026" or "17th September 2026"
    const clean = trimmed.replace(/(\d+)(st|nd|rd|th)/gi, '$1');
    const parsed = new Date(clean);
    if (!isNaN(parsed.getTime())) return parsed;
  } catch (e) {
    return null;
  }
  return null;
};

/**
 * Calculates human-readable timeline badge (Today, Tomorrow, In X days, Started)
 */
export const getTimelineBadge = (journeyDate) => {
  if (!journeyDate || journeyDate === 'N/A') return 'Scheduled';
  const start = parseAnyDate(journeyDate);
  if (!start) return 'Scheduled';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);

  const diffTime = start - today;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays < 0) return 'Started';
  return `In ${diffDays} days`;
};

/**
 * Calculates train delay in minutes and formatted label
 */
export const getDelayMinutes = (sch, act) => {
  if (!sch || !act) return null;
  if (sch === 'Source' || sch === 'Destination') return null;

  const parseTime = (timeStr) => {
    if (timeStr === act) return 0;
    try {
      const [time1] = sch.split(' ');
      const [time2] = act.split(' ');
      const [h1, m1] = time1.split(':').map(Number);
      const [h2, m2] = time2.split(':').map(Number);

      let min1 = h1 * 60 + m1;
      let min2 = h2 * 60 + m2;

      if (min2 - min1 < -720) min2 += 1440;
      if (min2 - min1 > 720) min1 += 1440;

      const diff = min2 - min1;
      return diff;
    } catch (e) {
      return 0;
    }
  };

  const diff = parseTime(sch);
  if (diff === 0) return { isDelayed: false, label: 'Right Time', color: 'text-green-600' };
  if (diff > 0) return { isDelayed: true, label: `${diff} min late`, color: 'text-red-600' };
  if (diff < 0) return { isDelayed: false, label: `${Math.abs(diff)} min early`, color: 'text-green-600' };
};

/**
 * Resolves current live train position from status string and station array
 */
export const getTrainStatusAndPosition = (statusStr, stations, currentStationCode) => {
  if (!statusStr || !stations) return { state: 'unknown', currentStationIdx: -1, isInTransit: false, message: '' };
  const norm = (s) => s?.toLowerCase().replace(/[^a-z0-9]/g, '');

  let state = 'unknown';
  let currentStationIdx = -1;
  let isInTransit = false;
  let message = '';

  const arrivedMatch = statusStr.match(/Arrived at (.*?) at/);
  const departedMatch = statusStr.match(/Departed from (.*?) at/);

  if (arrivedMatch) {
    state = 'arrived';
    const matchedName = arrivedMatch[1];
    currentStationIdx = stations.findIndex((s) => norm(s.station_name).includes(norm(matchedName)));
    if (currentStationIdx !== -1) message = `Arrived at ${stations[currentStationIdx].station_name}`;
  } else if (departedMatch) {
    state = 'departed';
    const matchedName = departedMatch[1];
    currentStationIdx = stations.findIndex((s) => norm(s.station_name).includes(norm(matchedName)));

    if (currentStationIdx === -1) {
      const nextMatch = statusStr.match(/Next stoppage station is (.*?)\./);
      if (nextMatch) {
        const nextName = nextMatch[1];
        const nextIdx = stations.findIndex((s) => norm(s.station_name).includes(norm(nextName)));
        if (nextIdx !== -1) {
          currentStationIdx = Math.max(0, nextIdx - 1);
          isInTransit = true;
          message = `Moving towards ${stations[nextIdx].station_name}`;
        }
      }
    } else {
      isInTransit = true;
      message = `Departed from ${stations[currentStationIdx].station_name}`;
    }
  }

  if (currentStationIdx === -1 && currentStationCode) {
    currentStationIdx = stations.findIndex((s) => norm(s.station_code) === norm(currentStationCode));
    if (currentStationIdx !== -1) {
      state = 'at_station';
      message = `At ${stations[currentStationIdx].station_name}`;
    }
  }

  return { currentStationIdx, isInTransit, message, state };
};

